import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { LocalizedLink } from './LocalizedLink'
import { cn } from '@/lib/utils'

/**
 * Cookie consent categories
 */
interface ConsentPreferences {
  essential: boolean
  analytics: boolean
  personalization: boolean
  marketing: boolean
}

const DEFAULT_PREFERENCES: ConsentPreferences = {
  essential: true, // Always required
  analytics: false,
  personalization: false,
  marketing: false,
}

const CONSENT_STORAGE_KEY = 'noberasco_cookie_consent'
const CONSENT_EXPIRY_DAYS = 365

interface StoredConsent {
  preferences: ConsentPreferences
  timestamp: number
}

/**
 * Get stored consent from localStorage
 */
function getStoredConsent(): StoredConsent | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (!stored) return null

    const parsed = JSON.parse(stored) as StoredConsent

    // Check if consent is expired
    const daysSinceConsent = (Date.now() - parsed.timestamp) / (1000 * 60 * 60 * 24)
    if (daysSinceConsent > CONSENT_EXPIRY_DAYS) {
      localStorage.removeItem(CONSENT_STORAGE_KEY)
      return null
    }

    return parsed
  } catch {
    return null
  }
}

/**
 * Save consent to localStorage
 */
function saveConsent(preferences: ConsentPreferences): void {
  if (typeof window === 'undefined') return

  const consent: StoredConsent = {
    preferences,
    timestamp: Date.now(),
  }
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent))
}

/**
 * Cookie preference modal component
 */
function ConsentModal({
  isOpen,
  onClose,
  preferences,
  onSave,
}: {
  isOpen: boolean
  onClose: () => void
  preferences: ConsentPreferences
  onSave: (preferences: ConsentPreferences) => void
}) {
  const { t } = useTranslation('common')
  const [localPrefs, setLocalPrefs] = useState(preferences)

  useEffect(() => {
    setLocalPrefs(preferences)
  }, [preferences])

  const handleToggle = (key: keyof Omit<ConsentPreferences, 'essential'>) => {
    setLocalPrefs((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleSave = () => {
    onSave(localPrefs)
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-lg max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('cookie.preferencesTitle')}
                </h3>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                  aria-label={t('aria.close')}
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6 overflow-y-auto">
                <p className="text-sm text-gray-600">
                  {t('cookie.preferencesDescription')}
                </p>

                {/* Cookie categories */}
                <div className="space-y-4">
                  {/* Essential - always on, cannot be disabled */}
                  <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-xl">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {t('cookie.categories.essential')}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {t('cookie.categories.essentialDesc')}
                      </p>
                    </div>
                    <div className="w-12 h-6 bg-gray-300 rounded-full relative">
                      <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow" />
                    </div>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-center justify-between py-3 px-4 rounded-xl border border-gray-200">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {t('cookie.categories.analytics')}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {t('cookie.categories.analyticsDesc')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleToggle('analytics')}
                      className={cn(
                        'w-12 h-6 rounded-full relative transition-colors',
                        localPrefs.analytics ? 'bg-primary' : 'bg-gray-300'
                      )}
                      aria-label={t('cookie.categories.toggleAnalytics')}
                      aria-pressed={localPrefs.analytics}
                    >
                      <div
                        className={cn(
                          'absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform',
                          localPrefs.analytics ? 'right-1' : 'left-1'
                        )}
                      />
                    </button>
                  </div>

                  {/* Personalization */}
                  <div className="flex items-center justify-between py-3 px-4 rounded-xl border border-gray-200">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {t('cookie.categories.personalization')}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {t('cookie.categories.personalizationDesc')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleToggle('personalization')}
                      className={cn(
                        'w-12 h-6 rounded-full relative transition-colors',
                        localPrefs.personalization ? 'bg-primary' : 'bg-gray-300'
                      )}
                      aria-label={t('cookie.categories.togglePersonalization')}
                      aria-pressed={localPrefs.personalization}
                    >
                      <div
                        className={cn(
                          'absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform',
                          localPrefs.personalization ? 'right-1' : 'left-1'
                        )}
                      />
                    </button>
                  </div>

                  {/* Marketing */}
                  <div className="flex items-center justify-between py-3 px-4 rounded-xl border border-gray-200">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {t('cookie.categories.marketing')}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {t('cookie.categories.marketingDesc')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleToggle('marketing')}
                      className={cn(
                        'w-12 h-6 rounded-full relative transition-colors',
                        localPrefs.marketing ? 'bg-primary' : 'bg-gray-300'
                      )}
                      aria-label={t('cookie.categories.toggleMarketing')}
                      aria-pressed={localPrefs.marketing}
                    >
                      <div
                        className={cn(
                          'absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform',
                          localPrefs.marketing ? 'right-1' : 'left-1'
                        )}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-full transition-colors"
                >
                  {t('cookie.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-full transition-colors"
                >
                  {t('cookie.savePreferences')}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

/**
 * Cookie Banner Component
 * GDPR-compliant cookie consent banner that appears on first visit.
 */
export function CookieBanner() {
  const { t, i18n } = useTranslation('common')
  const [showBanner, setShowBanner] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [preferences, setPreferences] = useState<ConsentPreferences>(DEFAULT_PREFERENCES)

  useEffect(() => {
    // Check if user has already given consent
    const stored = getStoredConsent()
    if (stored) {
      setPreferences(stored.preferences)
      // Consent already given, don't show banner
      return
    }

    // Show banner after a brief delay to not annoy users
    const timer = setTimeout(() => {
      setShowBanner(true)
    }, 1000)

    return () => clearTimeout(timer)
  }, [])

  const handleAcceptAll = useCallback(() => {
    const allAccepted: ConsentPreferences = {
      essential: true,
      analytics: true,
      personalization: true,
      marketing: true,
    }
    setPreferences(allAccepted)
    saveConsent(allAccepted)
    setShowBanner(false)
  }, [])

  const handleRejectAll = useCallback(() => {
    const onlyEssential: ConsentPreferences = {
      essential: true,
      analytics: false,
      personalization: false,
      marketing: false,
    }
    setPreferences(onlyEssential)
    saveConsent(onlyEssential)
    setShowBanner(false)
  }, [])

  const handleSavePreferences = useCallback((prefs: ConsentPreferences) => {
    setPreferences(prefs)
    saveConsent(prefs)
    setShowBanner(false)
  }, [])

  // Don't render if no banner needed
  if (!showBanner) {
    return null
  }

  const lang = i18n.language === 'it' ? 'it' : 'en'
  const cookiePolicyPath = lang === 'it' ? '/it/cookie-policy' : '/en/cookie-policy'

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          className="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6"
        >
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1),0_-10px_40px_-15px_rgba(0,0,0,0.15)] border border-gray-200 overflow-hidden">
              <div className="p-6 sm:flex sm:items-start sm:gap-6">
                {/* Content */}
                <div className="flex-1 mb-6 sm:mb-0">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('cookie.bannerTitle')}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {t('cookie.bannerMessage')}
                  </p>
                  <LocalizedLink
                    to={cookiePolicyPath}
                    className="inline-flex items-center text-sm text-primary hover:text-primary/80 font-medium mt-2"
                  >
                    {t('cookie.learnMore')}
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </LocalizedLink>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-3 sm:flex-nowrap sm:items-center">
                  <button
                    onClick={handleRejectAll}
                    className="w-full sm:w-auto px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    {t('cookie.rejectAll')}
                  </button>
                  <button
                    onClick={() => setShowModal(true)}
                    className="w-full sm:w-auto px-5 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-full transition-colors"
                  >
                    {t('cookie.customize')}
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="w-full sm:w-auto px-6 py-2.5 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-full transition-colors shadow-sm"
                  >
                    {t('cookie.acceptAll')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Preference modal */}
      <ConsentModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        preferences={preferences}
        onSave={handleSavePreferences}
      />
    </>
  )
}
